import { SelectionModel } from '@angular/cdk/collections';
import { AfterViewInit, Component, ElementRef, Inject, Injector, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { StateService } from '../../core/state.service';
import { User } from '../../models/user/user.model';

import { FeeGroupEnum, FeeGroupingOptions, InterchangeDescriptor } from '../../models/fee-groupings/fee-groupings.model';
import { FeeGroupingsService } from '../../models/fee-groupings/fee-groupings.service';
import { ClassifySelectionResult } from '../document-text-select/document-text-select.models';
import { createDragRef, DragRef } from '@angular/cdk/drag-drop';

@Component({
    selector: 'app-classify-pdf-selection-dialog',
    templateUrl: './classify-pdf-selection-dialog.component.html',
    styleUrls: ['./classify-pdf-selection-dialog.component.scss'],
    standalone: false
})
export class ClassifyPdfSelectionDialogComponent implements OnInit, AfterViewInit, OnDestroy {
    private paneDragRef: DragRef | null = null;
    private readonly RESIZE_MIN_WIDTH = 900;
    private readonly RESIZE_MIN_HEIGHT = 520;
    private resizeMousemoveRef: ((e: MouseEvent) => void) | null = null;
    private resizeMouseupRef: (() => void) | null = null;
    user: User;

    openProgramDropdown: boolean[] = [];
    bulkDropdownOpen = false;

    displayedColumns: string[] = ['select', 'feeLabel', 'feeGroupAbbrev', 'feeAmount', 'isFlagged', 'remove'];

    // matTableDataSource is the name of the data that will be used to populate the mat-table in the html file
    matTableDataSource = new MatTableDataSource<ClassifySelectionResult>([]);
    selection = new SelectionModel<ClassifySelectionResult>(true, []);

    interchangeProgramControl: UntypedFormControl[] = [];
    filteredPrograms: Observable<string[]>[] = [];
    programNames: string[];

    selectedOption: string | null = null;
    feeGroupingOptions = FeeGroupingOptions.filter(v => v.name !== FeeGroupEnum.PROCESSOR);
    readonly feeGroupEnum = FeeGroupEnum;

    constructor(
        public dialogRef: MatDialogRef<ClassifyPdfSelectionDialogComponent>,
        private feeService: FeeGroupingsService,
        private state: StateService,
        private injector: Injector,
        private elementRef: ElementRef<HTMLElement>,
        @Inject(MAT_DIALOG_DATA) public data: {
            passedInData: ClassifySelectionResult[],
            messageTitle?: string,
            message?: string,
            statementTotalFees?: number,
        },
    ) { }

    get sumOfListedFees(): number {
        const rows = this.matTableDataSource?.data ?? [];
        return rows.reduce((sum, row) => sum + (Number(row.feeAmount) || 0), 0);
    }

    ngOnInit() {
        this.state.getUser().then(user => this.user = user as User);

        if (!this.data?.passedInData) {
            this.dialogRef.close();
            document.body.classList.remove('loading');
            return;
        }

        this.data.passedInData = this.data.passedInData
            .map(x => new ClassifySelectionResult(x));

        const descriptorMap = new Map<string, string>();
        const descriptorByLabel = new Map<string, string>();
        this.feeService.interchangeDescriptorList.forEach(descriptor => {
            descriptorMap.set(descriptor.description, descriptor.programName);
            const key = descriptor.description.toLowerCase();
            if (!descriptorByLabel.has(key)) {
                descriptorByLabel.set(key, descriptor.programName);
            }
        });

        this.programNames = this.feeService.interchangeProgramList.map(el => el.name);
        /*
         * Get menu items ready for interchange programs --
         * We need a form controller and an Observable for each row.
         */
        this.data.passedInData.forEach(el => {
            el.feeProgram = descriptorMap.get(el.feeLabel) ?? descriptorByLabel.get(el.feeLabel.toLowerCase()) ?? null;
            const tmp = new UntypedFormControl(el.feeProgram ?? '');
            this.interchangeProgramControl.push(tmp);
            this.filteredPrograms.push(
                tmp.valueChanges.pipe(
                    startWith(''),
                    map((value: string) => this.programNames
                        .filter((program: string) => program.toLowerCase().includes((value ?? '').toLowerCase())))
                )
            );
        });

        // Write data to DOM
        this.matTableDataSource.data = this.data.passedInData;
    }

    ngAfterViewInit(): void {
        setTimeout(() => {
            this.attachDragRef();
            document.body.classList.remove('loading');
        }, 0);
    }

    private attachDragRef() {
        const pane = this.elementRef.nativeElement.closest('.cdk-overlay-pane') as HTMLElement;
        const boundary = (document.querySelector('.drag-boundary') || document.body) as HTMLElement;

        if (!pane || !boundary) {
            return;
        }

        pane.style.width = '1100px';
        pane.style.maxWidth = '1100px';

        this.attachDragRefForPane(pane);

        const resizeHandle = this.elementRef.nativeElement.querySelector('.dialog-resize-handle') as HTMLElement;
        if (resizeHandle) {
            resizeHandle.addEventListener('mousedown', (event: MouseEvent) => {
                this.onResizeStart(event, pane);
            });
        }
    }

    private onResizeStart(event: MouseEvent, pane: HTMLElement) {
        event.preventDefault();
        const startX = event.clientX;
        const startY = event.clientY;
        const startWidth = pane.offsetWidth;
        const startHeight = pane.offsetHeight;

        const container = pane.offsetParent as HTMLElement;
        const paneRect = pane.getBoundingClientRect();
        const containerRect = container?.getBoundingClientRect();

        const startLeft = containerRect ? paneRect.left - containerRect.left : pane.offsetLeft;
        const startTop = containerRect ? paneRect.top - containerRect.top : pane.offsetTop;
        const maxWidth = 1100;
        const maxHeight = 800;

        pane.style.transform = 'none';
        pane.style.right = 'auto';
        pane.style.bottom = 'auto';
        pane.style.left = startLeft + 'px';
        pane.style.top = startTop + 'px';
        this.resizeMousemoveRef = (moveEvent: MouseEvent) => {
            let newWidth = startWidth + (startX - moveEvent.clientX);
            let newHeight = startHeight + (startY - moveEvent.clientY);
            newWidth = Math.max(this.RESIZE_MIN_WIDTH, Math.min(maxWidth, newWidth));
            newHeight = Math.max(this.RESIZE_MIN_HEIGHT, Math.min(maxHeight, newHeight));

            let newLeft = startLeft + (startWidth - newWidth);
            let newTop = startTop + (startHeight - newHeight);

            // Keep dialog inside container: don't let top or left go past the top/left edge
            if (newTop < 0) {
                newHeight = startHeight + startTop;
                newTop = 0;
            }
            if (newLeft < 0) {
                newWidth = startWidth + startLeft;
                newLeft = 0;
            }

            pane.style.width = newWidth + 'px';
            pane.style.left = newLeft + 'px';
            pane.style.top = newTop + 'px';
        };

        this.resizeMouseupRef = () => {
            document.removeEventListener('mousemove', this.resizeMousemoveRef!);
            document.removeEventListener('mouseup', this.resizeMouseupRef!);
            this.resizeMousemoveRef = null;
            this.resizeMouseupRef = null;
            this.reattachDragRef(pane);
        };
        document.addEventListener('mousemove', this.resizeMousemoveRef);
        document.addEventListener('mouseup', this.resizeMouseupRef);
    }

    private attachDragRefForPane(pane: HTMLElement) {
        const boundary = (document.querySelector('.drag-boundary') || document.body) as HTMLElement;
        const handle = this.elementRef.nativeElement.querySelector('[cdkDragHandle]') as HTMLElement;

        if (!boundary) return;

        this.paneDragRef?.dispose();
        this.paneDragRef = createDragRef(this.injector, pane);
        this.paneDragRef.withBoundaryElement(boundary);
        if (handle) {
            this.paneDragRef.withHandles([handle]);
        }
    }

    private reattachDragRef(pane: HTMLElement) {
        this.attachDragRefForPane(pane);
    }

    flipAllFees() {
        this.data.passedInData.forEach(el => {
            el.feeAmount = -el.feeAmount;
        });
    }

    processFeeClassifications() {
        const dataRet: ClassifySelectionResult[] = this.matTableDataSource.data;

        const persistAndClose = (user?: User) => {
            if (user?.isAdmin) {
                const updates: InterchangeDescriptor[] = [];
                dataRet.forEach((result: ClassifySelectionResult) => {
                    if (result.feeProgram != null) {
                        const program = this.feeService.interchangeProgramList.find(subel => subel.name === result.feeProgram);
                        updates.push({
                            description: result.feeLabel,
                            programName: result.feeProgram,
                            programId: program?.id ?? ''
                        });
                    }
                });
                if (updates.length > 0) {
                    this.feeService.postInterchangeDescriptorList(updates);
                }
            }
            this.dialogRef.close(dataRet);
        };

        if (this.user != null) {
            persistAndClose(this.user);
        } else {
            this.state.getUser().then(persistAndClose);
        }
    }

    getErrorMsgForFeeAmt(amt: any) {
        if (amt.hasError('required')) {
            return 'You must enter a value';
        }

        return amt.hasError('feeAmt') ? 'Not a valid value - try 0' : '';
    }

    getErrorMsgForDesc(description: any) {
        if (description.hasError('required')) {
            return 'You must enter a description';
        }
        return description.hasError() ? 'Max 100 characters : {{desc.value?.length || 0}}/100' : '';
    }

    /** Whether the number of selected elements matches the total number of rows. */
    isAllSelected() {
        const numSelected = this.selection.selected.length;
        const numRows = this.matTableDataSource.data.length;
        return numSelected === numRows;
    }

    /** 
     * Used to disable the submit all button if the user removes all 
     * rows from the mat-select option drop down. 
     */
    isEmptyRecords() {
        return this.matTableDataSource.data.length === 0;
    }

    /** Selects all rows if they are not all selected; otherwise clear selection. */
    masterToggle() {
        if (this.isAllSelected()) {
            this.matTableDataSource.data.forEach(classifySelectionResult => {
                classifySelectionResult.optionSelected = false;
            });

            this.selection.clear();
            return;
        }

        this.selection.select(...this.matTableDataSource.data);
        this.matTableDataSource.data.forEach(classifySelectionResult => {
            classifySelectionResult.optionSelected = true;
        });
    }

    /** The label for the checkbox on the passed row */
    checkboxLabel(row?: ClassifySelectionResult): string {
        if (!row) {
            return `${this.isAllSelected() ? 'deselect' : 'select'} all`;
        }

        return `${this.selection.isSelected(row) ? 'deselect' : 'select'} row ${row.position + 1}`;
    }

    /**
     * Responds to radio button clicks
     */
    onGroupSelection(event, row: ClassifySelectionResult) {
        row.feeGroup = event.value;

    }

    /**
     * Responds to Select All Events
     */
    onSelectChange(event) {
        const selectedValue = event.value;

        if (selectedValue === 'removeRows') {
            const filteredList = this.matTableDataSource.data
                .filter(classifySelectionResult => !classifySelectionResult.optionSelected);
            this.matTableDataSource.data = filteredList;

            // reset the drop down to empty after a change event.
            this.selectedOption = null;
        } else if (selectedValue === 'Addtnl'
            || selectedValue === 'Assess'
            || selectedValue === 'NonNeg'
            || selectedValue === 'Intchg') {
            this.matTableDataSource.data.forEach(classifySelectionResult => {
                if (classifySelectionResult.optionSelected) {
                    classifySelectionResult.optionSelected = false;

                    // TODO (jacob): Why no processor?
                    switch (selectedValue) {
                        case 'Addtnl':
                            classifySelectionResult.feeGroup = FeeGroupEnum.ADDITIONAL;
                            break;
                        case 'Assess':
                            classifySelectionResult.feeGroup = FeeGroupEnum.ASSESSMENT;
                            break;
                        case 'NonNeg':
                            classifySelectionResult.feeGroup = FeeGroupEnum.NON_NEGOTIABLE;
                            break;
                        case 'Intchg':
                            classifySelectionResult.feeGroup = FeeGroupEnum.INTERCHANGE;
                            break;
                    }

                }
            });

            // reset the drop down to empty after a change event.
            this.selectedOption = null;

            // clears the select all mat-checkbox.
            this.selection.clear();
        }
    }

    isAnyOptionsSelected() {
        if (this.matTableDataSource?.data) {
            for (const classifySelectionResult of this.matTableDataSource.data) {
                if (classifySelectionResult.optionSelected) {
                    return true;
                }
            }
        }
        return false;
    }

    removeFee(row: ClassifySelectionResult) {
        const matTableData = this.matTableDataSource.data;
        const index = matTableData.indexOf(row);
        if (index === -1) return;

        this.matTableDataSource.data = matTableData.filter((_, i) => i !== index);
        this.interchangeProgramControl.splice(index, 1);
        this.filteredPrograms.splice(index, 1);
        this.selection.deselect(row);
    }

    bulkOptionLabel(value: string | null): string {
        switch (value) {
            case 'removeRows': return 'Remove row(s)';
            case 'Addtnl': return '"Addtnl" Fee Classification';
            case 'Assess': return '"Assess" Fee Classification';
            case 'NonNeg': return '"NonNeg" Fee Classification';
            case 'Intchg': return '"Intchg" Fee Classification';
            default: return 'None';
        }
    }

    chooseBulkOption(value: string | null) {
        this.bulkDropdownOpen = false;
        this.onSelectChange({ value });
    }

    // New helpers for the program autocomplete dropdown:
    selectProgram(i: number, program: string) {
        this.interchangeProgramControl[i].setValue(program);
        this.openProgramDropdown[i] = false;
    }

    closeProgramDropdownSoon(i: number) {
        // delay so a mousedown on an option registers before the panel closes
        setTimeout(() => { this.openProgramDropdown[i] = false; }, 150);
    }

    ngOnDestroy(): void {
        if (this.resizeMousemoveRef) {
            document.removeEventListener('mousemove', this.resizeMousemoveRef);
        }
        if (this.resizeMouseupRef) {
            document.removeEventListener('mouseup', this.resizeMouseupRef);
        }
        this.resizeMousemoveRef = null;
        this.resizeMouseupRef = null;
        this.paneDragRef?.dispose();
        this.paneDragRef = null;
        document.body.classList.remove('loading');
    }
}